import { http } from '@/interceptors/axiosRequestInterceptor';
import { ApiResponse2, Comment } from '@/models';
import type { CommentFormData } from '@/types/comments';
import { computed, nextTick, ref } from 'vue';

const commentTime = (c: Pick<Comment, 'created_at'>): number => {
  const t = new Date(c.created_at).getTime();
  return Number.isNaN(t) ? 0 : t;
};

export const newestFirst = (
  a: Pick<Comment, 'id' | 'created_at'>,
  b: Pick<Comment, 'id' | 'created_at'>
): number => commentTime(b) - commentTime(a) || Number(b.id) - Number(a.id);

export const useComments = () => {
  const comments = ref<Comment[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const treeVersion = ref(0);

  const commentTree = computed(() => {
    treeVersion.value;

    const allComments = [...comments.value];

    if (allComments.length === 0) {
      return [];
    }

    const commentMap = new Map();
    const rootComments = [];

    allComments.forEach((comment) => {
      commentMap.set(comment.id, { ...comment, children: [] });
    });

    allComments.forEach((comment) => {
      if (comment.parent_message_id) {
        const parent = commentMap.get(Number(comment.parent_message_id));
        if (parent) {
          parent.children.push(commentMap.get(comment.id));
        } else {
          rootComments.push(commentMap.get(comment.id));
        }
      } else {
        rootComments.push(commentMap.get(comment.id));
      }
    });

    // AB#22937 (Zendesk #10378) — newest first at every level, replies included,
    // so the latest customer/supplier update is at the top of a long thread
    // instead of at the bottom. Ties fall back to the higher id (created later).
    rootComments.sort(newestFirst);

    const sortChildren = (comment: Comment & { children: Comment[] }) => {
      if (comment.children && comment.children.length > 0) {
        comment.children.sort(newestFirst);
        comment.children.forEach(sortChildren);
      }
    };

    rootComments.forEach(sortChildren);

    return rootComments;
  });

  const updateComments = (newComments: Comment[]) => {
    comments.value = [...newComments];
    treeVersion.value += 1;
  };

  const addComment = async (formData: CommentFormData, apiUrl: string, order_item_id: number): Promise<Comment> => {
    loading.value = true;
    error.value = null;

    try {
      const form = new FormData();

      if (formData.message && formData.message.trim()) {
        form.append('message', formData.message.trim());
      }

      if (formData.parent_message_id) {
        form.append('parent_message_id', formData.parent_message_id.toString());
        if (formData.parent_message) {
          form.append('parent_message', formData.parent_message);
        }
      }

      if (formData.files && formData.files.length > 0) {
        formData.files.forEach((file) => {
          form.append('files[]', file);
        });
      }

      if (!formData.message?.trim() && (!formData.files || formData.files.length === 0)) {
        throw new Error('Comment must have either a message or files');
      }
      form.append('order_item_id', order_item_id.toString())
      const response = await http.post<ApiResponse2<{ item_activity_comment: Comment }>>(
        apiUrl,
        form,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const newComment = response.data.result.item_activity_comment;

      if (formData.parent_message_id && !newComment.parent_message_id) {
        newComment.parent_message_id = formData.parent_message_id;
        newComment.parent_message = formData.parent_message;
      }

      comments.value.push(newComment);
      treeVersion.value += 1;

      await nextTick();
      return newComment;
    } catch (err: any) {
      error.value = err.message || err.response?.data?.message || 'Failed to add comment';
      throw err;
    } finally {
      loading.value = false;
    }
  };
  const updateComment = async (commentId: number, formData: CommentFormData, apiUrl: string, editCommentData: Comment, order_item_id : number): Promise<Comment> => {
    loading.value = true;
    error.value = null;

    try {
      const form = new FormData();
      form.append('message', formData.message);
      form.append('order_item_id', order_item_id.toString());
      if (formData.files && formData.files.length > 0) {
        formData.files.forEach((file) => {
          const isAlreadyUploaded = editCommentData.files?.some(
            (existingFile) => existingFile.url && existingFile.url.includes(file.name)
          );
          if (!isAlreadyUploaded) {
            form.append('files[]', file);
          }
        });
      }
      if (formData.removed_files && formData.removed_files.length > 0) {
        formData.removed_files.forEach((file) => {
          form.append('removed_files[]', file);
        });
      }

      const response = await http.post<ApiResponse2<{ item_activity_comment: Comment }>>(
        `${apiUrl}/${commentId}`,
        form,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const updatedComment = response.data.result.item_activity_comment;

      const index = comments.value.findIndex((c) => c.id === commentId);
      if (index !== -1) {
        const newComments = [...comments.value];
        newComments[index] = updatedComment;
        updateComments(newComments);
      }

      await nextTick();
      return updatedComment;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update comment';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const deleteComment = async (commentId: number): Promise<void> => {
    loading.value = true;
    error.value = null;

    try {
      await http.delete(`order_item/${commentId}/delete_comment`);

      const newComments = comments.value.filter((c) => c.id !== commentId);
      updateComments(newComments);
      await nextTick();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to delete comment';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const resolveComment = async (commentId: number, actionTaken: boolean): Promise<void> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await http.put<ApiResponse2<{ comment: Comment }>>(
        `order_item/comment/${commentId}/take_action`,
        { action_taken: actionTaken }
      );

      const updatedComment = response.data.result.comment;
      const index = comments.value.findIndex((c) => c.id === commentId);
      if (index !== -1) {
        const newComments = [...comments.value];
        Object.assign(newComments[index], updatedComment);
        updateComments(newComments);
      }

      await nextTick();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to resolve comment';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    comments,
    commentTree,
    loading,
    error,
    addComment,
    updateComment,
    deleteComment,
    resolveComment,
    updateComments,
  };
}
